/* eslint-disable max-lines-per-function, max-params, max-lines */
import { api, toQueryString } from "jiffy-api";
import getVariantInfo, { buildItemIdentifiers, getItemPrice } from "common/jiffy/get-variant";
import { sendDataLayer, track } from "jiffy-analytics";
import { clearCachedEstimates } from "components/jiffy/product-details/cache-composition-delivery-date";
import { isMadePdpOrMadeMode } from "components/jiffy/vue-product-details-print/utils/made-utils";
import idleCallback from "../../../common/utils/ui/idle_callback";
import productPage from "./product-page";

// Getters
const getIsBuyItAgainStatus = () => !!document.getElementById("js-buy-it-again-modal");
const $getCartName = () => document.querySelector(".js-continue-shopping-modal-cart-name");
const $getCutOff = () => document.querySelector(".js-product-details-delivery-dates-cutoff");
const $productRef = () => document.querySelector(productPage.selectors.productReference);
const $quantityFormInput = () => document.querySelector(productPage.selectors.quantityFormInput);
const $getActiveColor = () => document.getElementById("active-color-name");

export const sendSocialMediaConversionApiData = async (
  content,
  selectedColor,
  viewContentEventId,
  pageViewEventId,
  eventName
) => {
  const queryString = toQueryString({ ac: selectedColor });
  await api.product.sendSocialMediaConversionApi({
    content,
    page_view_event_id: pageViewEventId,
    view_content_event_id: viewContentEventId,
    event_name: eventName,
    source_url: window.location.href.replace(/\?.*/, `?${queryString}`)
  });
};

const sendPdpVisit = async (variantInputs, variantId, skus, selectedColor, isMadePdp = false) => {
  const cartFirstVariant =
    variantInputs.find(item => item.dataset.isStandardSize) || variantInputs[0];
  const variant = await getVariantInfo(variantId, isMadePdp);
  const fbData = JSON.parse(variant.fbData);
  const viewContentEventId = `VC-${fbData.external_id[0]}-${Date.now()}`;
  const pageViewEventId = `PV-${fbData.external_id[0]}-${Date.now()}`;

  let updatedSkus = skus;
  const madeVariantSku = variantInputs[0].dataset.printVariantSku;

  if (isMadePdp) {
    updatedSkus = variantInputs.map(item => getMadeSku(item.dataset.sku, madeVariantSku));
  }

  const fbContent = {
    content_ids: updatedSkus,
    content_category: variant.product.adwords_category,
    content_name: variant.product.name,
    content_type: "product",
    user_data: fbData
  };

  const {
    catalogNumber: firstCatalogNumber,
    color: firstColor,
    detailedProductName: firstDetailedProductName,
    brandName: firstBrandName,
    madeOnePlacementPrice
  } = cartFirstVariant.dataset;

  const firstPrice = getItemPrice(cartFirstVariant.dataset.amount, madeOnePlacementPrice);

  const { itemId, itemName } = buildItemIdentifiers(
    firstCatalogNumber,
    firstColor,
    isMadePdp,
    firstDetailedProductName
  );

  const itemsViewed = [
    {
      id: itemId,
      name: itemName,
      brand: firstBrandName,
      price: firstPrice,
      google_business_vertical: "retail"
    }
  ];
  const viewItemValue = firstPrice;

  const currentItem = variantInputs[0];
  let ecommerceData;
  if (currentItem) {
    const { catalogNumber, color, detailedProductName, brandName, googleAdsCategory, currency } =
      currentItem.dataset;

    const price = getItemPrice(currentItem.dataset.amount, madeOnePlacementPrice);

    const { itemId: currentItemId, itemName: currentItemName } = buildItemIdentifiers(
      catalogNumber,
      color,
      isMadePdp,
      detailedProductName
    );

    ecommerceData = {
      ecommerce: {
        currency,
        value: Number(price),
        items: [
          {
            item_id: currentItemId,
            item_name: currentItemName,
            item_brand: brandName,
            item_category: googleAdsCategory,
            price
          }
        ]
      }
    };
  } else {
    ecommerceData = {};
  }

  sendSocialMediaConversionApiData(
    fbContent,
    selectedColor,
    viewContentEventId,
    pageViewEventId,
    "ViewContent"
  );

  let items = variantInputs.map(item => ({ product_id: item.dataset.sku }));
  if (isMadePdp) {
    items = items.map(item => ({
      product_id: getMadeSku(item.product_id, madeVariantSku)
    }));
  }

  sendDataLayer({
    event: "PDPPageVisit",
    items,
    msAdProductIds: updatedSkus,
    itemsViewed,
    viewItemValue,
    fbContent,
    viewContentEventId,
    pageViewEventId,
    ...ecommerceData
  });
};

let productAddedModalData = {};
export const addToCartAnalytics = async (target, isMadePdp) => {
  sendDataLayer({
    genericEvent: true,
    event: "click",
    eventCategory: "UX",
    eventLabel: "add to cart"
  });

  const itemQuantities = Object.entries(target.itemsQty);
  const cartFirstVariantId = itemQuantities[0][0];
  const cartFirstVariant = await getVariantInfo(cartFirstVariantId, isMadePdp);
  const cartSkus = [];
  const cartItems = [];
  let addToCartValue = 0;
  const itemsAdded = [];
  const ecommerceItemsAdded = [];
  const itemAddedMixpanel = [];
  productAddedModalData = {};
  let variantInfo;
  let item;
  /* eslint-disable no-await-in-loop, no-restricted-syntax */
  for (const [variantId, quantity] of itemQuantities) {
    variantInfo = await getVariantInfo(variantId, isMadePdp);
    cartSkus.push(variantInfo.sku);
    cartItems.push({ product_id: variantInfo.sku });
    addToCartValue += quantity * variantInfo.price;
    item = {
      id: variantInfo.product.item_group_id,
      name: variantInfo.product.name,
      brand: variantInfo.product.brand,
      variant: variantInfo.color,
      price: variantInfo.price,
      currency: variantInfo.currency,
      size: variantInfo.size
    };
    itemsAdded.push({
      id: item.id,
      name: item.name,
      brand: item.brand,
      variant: item.color,
      price: item.price,
      quantity
    });
    ecommerceItemsAdded.push({
      item_id: item.id,
      item_name: item.name,
      item_brand: item.brand,
      item_variant: item.variant,
      price: item.price,
      currency: item.currency,
      size: item.size,
      quantity
    });
    itemAddedMixpanel.push({
      quantity,
      sku: variantInfo.sku,
      spree_composition_id: variantInfo.compositionId,
      composition: variantInfo.color,
      favorite: variantInfo.favorite,
      fulfillment_group: variantInfo.fulfillmentGroup
    });

    const trackProperties = {
      brand: variantInfo.product.brand,
      product_category: variantInfo.product.adwords_category,
      product: variantInfo.product.name,
      spree_composition_id: variantInfo.compositionId,
      composition: variantInfo.color,
      fulfillment_group: variantInfo.fulfillmentGroup || "",
      sku: variantInfo.sku,
      quantity,
      buy_it_again: getIsBuyItAgainStatus(),
      favorite: variantInfo.favorite,
      softness_score: variantInfo.softnessScore,
      business_line: variantInfo.product.business_line
    };

    if (target.dtfSizes) {
      trackProperties.dtf_width = target.dtfSizes.width;
      trackProperties.dtf_height = target.dtfSizes.height;
      trackProperties.dtf_area = target.dtfSizes.area;
    }

    track("product_added", trackProperties);
  }

  const $container = document.querySelector("#product-page-container");
  const { orderEmail: email } = $container ? $container.dataset : {};
  const { brand, name, adwordsCategory, color, compositionId } = cartFirstVariant.product;

  addToCartValue = Number(addToCartValue.toFixed(2));
  const fbContent = {
    content_name: cartFirstVariant.product.name,
    content_category: cartFirstVariant.product.adwords_category,
    content_ids: cartSkus,
    content_type: "product",
    user_data: JSON.parse(cartFirstVariant.fbData)
  };
  const { eventId: fbAddToCartEventId } = target;
  sendDataLayer({
    event: "AddToCart",
    cartItems,
    itemsAdded,
    addToCartValue,
    email,
    fbContent,
    addToCartEventId: fbAddToCartEventId,
    ecommerce: {
      value: addToCartValue,
      items: ecommerceItemsAdded
    }
  });

  if (target.isBulk) {
    track("product_added_bulk_form", {
      brand: cartFirstVariant.product.brand,
      product: cartFirstVariant.product.name,
      product_category: cartFirstVariant.product.adwords_category,
      items: itemAddedMixpanel,
      business_line: cartFirstVariant.product.business_line
    });
  }

  productAddedModalData = {
    brand,
    product: name,
    product_category: adwordsCategory,
    composition_id: compositionId,
    composition: color,
    items_added: itemAddedMixpanel,
    items_value: addToCartValue,
    business_line: cartFirstVariant.product.business_line
  };

  clearCachedEstimates();
};

export const triggerPdpVisitEvent = (activeColor = "", isMadePdp = false) => {
  const variantId = $(`#product-details-variants-form input[data-color="${activeColor}"]`)
    .first()
    .data("variant-id");

  const variantInputs = Array.from(
    document.querySelectorAll(`#product-details-variants-form input[data-color="${activeColor}"]`)
  );

  const skus = variantInputs.map(item => item.dataset.sku);

  if (skus.length) {
    idleCallback(() => {
      sendPdpVisit(variantInputs, variantId, skus, activeColor, isMadePdp);
    });
  }

  return variantId;
};

export const trackProductAddedModal = (action, trackingAdditionalProps) => {
  const props = {
    ...productAddedModalData,
    ...trackingAdditionalProps,
    action,
    cart_name: $getCartName().textContent,
    cart_delivery_date_valid: $getCutOff().textContent
  };
  track("product_added_modal", props);
};

export const trackSizeQuantityMissed = () => {
  const catalogNumber = $productRef()?.dataset?.productId;
  const compositionName = $getActiveColor().value;
  const businessLine = isMadePdpOrMadeMode()
    ? "Print"
    : $quantityFormInput()?.dataset?.businessLine;

  track("pdp_size_quantity_missed", {
    catalog_number: catalogNumber,
    business_line: businessLine,
    composition_name: compositionName
  });
};

const getMadeSku = (sku, madeSku) => `${sku}-${madeSku}`;
